import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { 
  Users, UserPlus, Trash2, Key, Shield, ShieldCheck, User, Pencil, Mail,
  TrendingUp, ShoppingBag, FileSignature, Landmark, Scale, Settings, Check
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configuration des modules disponibles
const MODULES = [
  { id: "ventes", label: "Ventes", icon: TrendingUp, color: "text-green-600", description: "Factures émises, clients, analyse CA, vigilance" },
  { id: "achats", label: "Achats", icon: ShoppingBag, color: "text-orange-600", description: "Factures fournisseurs, import en masse" },
  { id: "contrats", label: "Contrats", icon: FileSignature, color: "text-purple-600", description: "Portefeuille, échéances, détail financier" },
  { id: "rapprochement", label: "Rapprochement bancaire", icon: Landmark, color: "text-blue-600", description: "Relevés, remises SEPA, mandats" },
  { id: "droit_societes", label: "Droit des sociétés", icon: Scale, color: "text-slate-600", description: "Documents juridiques" }
];

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Europe/Paris' });
};

export default function AdminUsers() {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "user", permissions: [] });
  const [creating, setCreating] = useState(false);
  
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState({ id: "", name: "", email: "" });
  const [saving, setSaving] = useState(false);
  
  // Permissions modal
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/users`, { headers });
      setUsers(res.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      if (error.response?.status === 403) {
        toast.error("Accès non autorisé");
      } else {
        toast.error("Erreur lors du chargement des utilisateurs");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    try {
      setCreating(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/users`, newUser, { headers });
      toast.success("Utilisateur créé avec succès");
      setCreateModalOpen(false);
      setNewUser({ name: "", email: "", password: "", role: "user", permissions: [] });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/users/${userId}?is_active=${!currentActive}`, {}, { headers });
      toast.success(currentActive ? "Utilisateur désactivé" : "Utilisateur activé");
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleToggleRole = async (userId, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/admin/users/${userId}?role=${newRole}`, {}, { headers });
      toast.success(`Rôle changé en ${newRole === "admin" ? "Administrateur" : "Utilisateur"}`);
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    
    try {
      setResetting(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/users/${resetUserId}/reset-password?new_password=${encodeURIComponent(newPassword)}`, {}, { headers });
      toast.success("Mot de passe réinitialisé");
      setResetModalOpen(false);
      setResetUserId(null);
      setNewPassword("");
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setDeleting(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/users/${deleteUserId}`, { headers });
      toast.success("Utilisateur supprimé");
      setDeleteModalOpen(false);
      setDeleteUserId(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  const handleEditUser = async () => {
    if (!editUser.name || !editUser.email) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    try {
      setSaving(true);
      const headers = { Authorization: `Bearer ${token}` };
      const params = new URLSearchParams();
      params.append('name', editUser.name);
      params.append('email', editUser.email);
      
      await axios.put(`${API}/admin/users/${editUser.id}?${params}`, {}, { headers });
      toast.success("Utilisateur modifié avec succès");
      setEditModalOpen(false);
      setEditUser({ id: "", name: "", email: "" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (user) => {
    setEditUser({ id: user.id, name: user.name, email: user.email });
    setEditModalOpen(true);
  };
  
  const openPermissionsModal = (user) => {
    setPermissionsUser(user);
    setSelectedPermissions(user.permissions || []);
    setPermissionsModalOpen(true);
  };
  
  const handleTogglePermission = (moduleId) => {
    setSelectedPermissions(prev => 
      prev.includes(moduleId) 
        ? prev.filter(p => p !== moduleId)
        : [...prev, moduleId]
    );
  };
  
  const handleSelectAllPermissions = () => {
    if (selectedPermissions.length === MODULES.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions(MODULES.map(m => m.id));
    }
  };
  
  const handleSavePermissions = async () => {
    if (!permissionsUser) return;
    
    try {
      setSavingPermissions(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/admin/users/${permissionsUser.id}/permissions`,
        selectedPermissions,
        { headers }
      );
      toast.success("Permissions mises à jour");
      setPermissionsModalOpen(false);
      setPermissionsUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSavingPermissions(false);
    }
  };
  
  const handleNewUserPermissionToggle = (moduleId) => {
    setNewUser(prev => ({
      ...prev,
      permissions: prev.permissions.includes(moduleId)
        ? prev.permissions.filter(p => p !== moduleId)
        : [...prev.permissions, moduleId]
    }));
  };

  const getPermissionsBadges = (user) => {
    if (user.role === "admin") {
      return <Badge className="bg-blue-100 text-blue-700 text-xs">Tous les accès</Badge>;
    }
    const perms = user.permissions || [];
    if (perms.length === 0) {
      return <Badge variant="outline" className="text-xs text-red-500 border-red-200">Aucun accès</Badge>;
    }
    if (perms.length === MODULES.length) {
      return <Badge className="bg-green-100 text-green-700 text-xs">Tous les modules</Badge>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {perms.slice(0, 2).map(p => {
          const mod = MODULES.find(m => m.id === p);
          return mod ? (
            <Badge key={p} variant="outline" className="text-xs">
              {mod.label}
            </Badge>
          ) : null;
        })}
        {perms.length > 2 && (
          <Badge variant="outline" className="text-xs">+{perms.length - 2}</Badge>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Gestion des Utilisateurs
          </h1>
          <p className="text-muted-foreground text-sm">
            {users.length} utilisateur{users.length > 1 ? 's' : ''} • Gérez les accès et permissions
          </p>
        </div>
        <Button
          onClick={() => setCreateModalOpen(true)}
          className="btn-primary bg-primary text-primary-foreground"
          data-testid="create-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Créer un utilisateur
        </Button>
      </div>

      {/* Users Table */}
      <Card className="bg-white border">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-14 skeleton rounded"></div>
              ))}
            </div>
          ) : (
            <div className="table-responsive overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="text-xs uppercase text-muted-foreground font-medium">Utilisateur</TableHead>
                  <TableHead className="text-xs uppercase text-muted-foreground font-medium hidden sm:table-cell">Email</TableHead>
                  <TableHead className="text-xs uppercase text-muted-foreground font-medium hidden md:table-cell">Rôle</TableHead>
                  <TableHead className="text-xs uppercase text-muted-foreground font-medium hidden lg:table-cell">Permissions</TableHead>
                  <TableHead className="text-xs uppercase text-muted-foreground font-medium">Statut</TableHead>
                  <TableHead className="text-xs uppercase text-muted-foreground font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user, index) => (
                  <TableRow key={user.id} className="table-row-hover" data-testid={`user-row-${index}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                          <span className="text-sm font-medium text-blue-600">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-foreground block truncate">{user.name}</span>
                          <span className="text-xs text-muted-foreground sm:hidden truncate block">{user.email}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden sm:table-cell">{user.email}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${user.role === 'admin' ? 'bg-blue-50 text-blue-600 border-blue-200' : ''}`}
                      >
                        {user.role === 'admin' ? (
                          <><ShieldCheck className="w-3 h-3 mr-1" /> Admin</>
                        ) : (
                          <><User className="w-3 h-3 mr-1" /> Utilisateur</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {getPermissionsBadges(user)}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${user.is_active !== false ? 'status-active' : 'status-inactive'}`}
                      >
                        {user.is_active !== false ? 'Actif' : 'Désactivé'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(user)}
                          className="text-muted-foreground hover:text-foreground"
                          title="Modifier"
                          data-testid={`edit-user-btn-${index}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        
                        {/* Bouton Permissions - uniquement pour les non-admins */}
                        {user.role !== 'admin' && user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPermissionsModal(user)}
                            className="text-muted-foreground hover:text-purple-600"
                            title="Gérer les permissions"
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleRole(user.id, user.role)}
                            className="text-muted-foreground hover:text-foreground"
                            title={user.role === 'admin' ? "Retirer les droits admin" : "Promouvoir admin"}
                          >
                            <Shield className={`w-4 h-4 ${user.role === 'admin' ? 'text-blue-600' : ''}`} />
                          </Button>
                        )}
                        
                        {user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(user.id, user.is_active !== false)}
                            className="text-muted-foreground hover:text-foreground"
                            title={user.is_active !== false ? "Désactiver" : "Activer"}
                          >
                            <Switch 
                              checked={user.is_active !== false} 
                              className="pointer-events-none scale-75"
                            />
                          </Button>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setResetUserId(user.id); setResetModalOpen(true); }}
                          className="text-muted-foreground hover:text-foreground"
                          title="Réinitialiser mot de passe"
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        
                        {user.id !== currentUser?.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setDeleteUserId(user.id); setDeleteModalOpen(true); }}
                            className="text-muted-foreground hover:text-red-500"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Créer un utilisateur
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Définissez les informations et les accès du nouvel utilisateur.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm text-muted-foreground">Nom complet</Label>
                <Input
                  id="name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Jean Dupont"
                  data-testid="new-user-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm text-muted-foreground">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="jean@betaye.fr"
                  data-testid="new-user-email"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm text-muted-foreground">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="new-user-password"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Rôle</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v, permissions: v === 'admin' ? MODULES.map(m => m.id) : newUser.permissions })}>
                  <SelectTrigger data-testid="new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Utilisateur</SelectItem>
                    <SelectItem value="admin">Administrateur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Permissions - uniquement si pas admin */}
            {newUser.role !== 'admin' && (
              <div className="space-y-3 pt-2">
                <Label className="text-sm text-muted-foreground">Modules accessibles</Label>
                <div className="grid grid-cols-1 gap-2 p-3 bg-slate-50 rounded-lg">
                  {MODULES.map(mod => {
                    const Icon = mod.icon;
                    const isChecked = newUser.permissions.includes(mod.id);
                    return (
                      <div 
                        key={mod.id}
                        className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${isChecked ? 'bg-white shadow-sm' : 'hover:bg-slate-100'}`}
                        onClick={() => handleNewUserPermissionToggle(mod.id)}
                      >
                        <Checkbox 
                          checked={isChecked}
                          onCheckedChange={() => handleNewUserPermissionToggle(mod.id)}
                        />
                        <Icon className={`w-4 h-4 ${mod.color}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{mod.label}</p>
                          <p className="text-xs text-muted-foreground">{mod.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {newUser.role === 'admin' && (
              <p className="text-sm text-blue-600 bg-blue-50 p-3 rounded-lg flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" />
                Les administrateurs ont accès à tous les modules.
              </p>
            )}
            
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setCreateModalOpen(false)}>
                Annuler
              </Button>
              <Button type="submit" className="btn-primary bg-primary text-primary-foreground" disabled={creating} data-testid="submit-create-user">
                {creating ? "Création..." : "Créer l'utilisateur"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Permissions Modal */}
      <Dialog open={permissionsModalOpen} onOpenChange={setPermissionsModalOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-600" />
              Permissions de {permissionsUser?.name}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Sélectionnez les modules accessibles pour cet utilisateur.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {selectedPermissions.length}/{MODULES.length} modules sélectionnés
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleSelectAllPermissions}
              >
                {selectedPermissions.length === MODULES.length ? "Tout désélectionner" : "Tout sélectionner"}
              </Button>
            </div>
            
            <div className="space-y-2">
              {MODULES.map(mod => {
                const Icon = mod.icon;
                const isChecked = selectedPermissions.includes(mod.id);
                return (
                  <div 
                    key={mod.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      isChecked 
                        ? 'border-purple-200 bg-purple-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => handleTogglePermission(mod.id)}
                  >
                    <Checkbox 
                      checked={isChecked}
                      onCheckedChange={() => handleTogglePermission(mod.id)}
                    />
                    <Icon className={`w-5 h-5 ${mod.color}`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{mod.label}</p>
                      <p className="text-xs text-muted-foreground">{mod.description}</p>
                    </div>
                    {isChecked && <Check className="w-4 h-4 text-purple-600" />}
                  </div>
                );
              })}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setPermissionsModalOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleSavePermissions} 
                className="bg-purple-600 hover:bg-purple-700 text-white"
                disabled={savingPermissions}
              >
                {savingPermissions ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={resetModalOpen} onOpenChange={setResetModalOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600" />
              Réinitialiser le mot de passe
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-sm text-muted-foreground">Nouveau mot de passe</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="reset-password-input"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetModalOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleResetPassword} className="btn-primary bg-primary text-primary-foreground" disabled={resetting}>
                {resetting ? "Réinitialisation..." : "Réinitialiser"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Supprimer l'utilisateur
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Cette action est irréversible. L'utilisateur ne pourra plus se connecter.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleDeleteUser} variant="destructive" disabled={deleting}>
              {deleting ? "Suppression..." : "Supprimer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              Modifier l'utilisateur
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name" className="text-sm text-muted-foreground">Nom complet</Label>
              <Input
                id="edit-name"
                value={editUser.name}
                onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                placeholder="Jean Dupont"
                data-testid="edit-user-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-email" className="text-sm text-muted-foreground flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Adresse email
              </Label>
              <Input
                id="edit-email"
                type="email"
                value={editUser.email}
                onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                placeholder="jean.dupont@betaye.fr"
                data-testid="edit-user-email"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleEditUser} className="btn-primary bg-primary text-primary-foreground" disabled={saving}>
                {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
